/* eslint-disable camelcase */
import { isArray, isBoolean, isNumber, isObject, isString } from 'typechecker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Price from '@/classes/Price'
import ReferralAds from '@/enums/ReferralAds'
import Vendor from '@/enums/Vendor'
import PaymentStatus from '@/enums/PaymentStatus'
import PaymentType from '@/enums/PaymentType'
import PaymentMethod from '@/enums/PaymentMethod'
import { ReservationResponse, PaymentMethod as ReservationPaymentMethod, CardPaymentVender, Erp, DiscountMethod, reverseReservationStatus, ApiReservationStatus } from '@/types/ggo-api/responses/v2/Reservation'

dayjs.extend(utc)
dayjs.extend(timezone)

export type OrderStatus = 'alive' | 'delivered' | 'complete' | 'canceled' | 'hold'

export type OrderArgument = {
  orderNum?: string;
  erpOrderNum?: string;
  startStoreId?: number | string;
  startStoreName?: string;
  endStoreId?: number | string;
  endStoreName?: string;
  car_id?: number;
  orderTime?: string;
  startTime?: string;
  endTime?: string;
  insuranceFee?: number;
  status?: OrderStatus
  cancel_at?: string;
  vendor?: Vendor;
  productName?: string;

  email?: string;
  price?: Price;
  insuranceDataFiled?: number
  paid?: PaymentStatus
  insuranceIndex?: string
  invoice_to? : string
  invoice_code? : number
  invoice_name? : string
  invoice_no? : string
  pay_time? : string
  credit_last_digits? : number
  payment_methods_id? : number
  refundPercentage?: number
  refund? : {
    value: number,
    currency: {
      code: string,
      sign: string,
    }
  }
  referralAds?: Array<number>

  isCustomerFilled?: boolean;
  isStoreFilled?: boolean;
  isOverDeadline?: boolean;
  customerScore?: any;

  payment_type?: number;

  isReservation?: boolean;
}

export default class Order {
  key?: Symbol = Symbol('')
  /** 訂單編號 */
  orderNum?: string
  /** ERP 訂單編號 */
  erpOrderNum?: string
  /** 租車店家id */
  startStoreId?: number | string
  /** 租車店家名稱 */
  startStoreName?: string
  /** 還車店家id */
  endStoreId?: number | string
  /** 還車店家名稱 */
  endStoreName?: string
  /** 車輛id */
  carId?: number
  /** 下單時間 */
  orderTime?: string
  /** 租車時間 */
  startTime?: string
  /** 還車時間 */
  endTime?: string
  /** 取消時間 */
  cancelAt?: string
  /** 保險金額 */
  insuranceFee?: number
  /** 訂單狀態 */
  status?: OrderStatus
  /** vendor */
  vendor?: Vendor
  /** 車輛型號 */
  productName?: string

  email?: string
  price?: Price
  insuranceDataFiled?: boolean
  paid?: number
  insuranceIndex?: string
  invoiceTo?: string
  invoiceCode?: number
  invoiceName?: string
  invoiceNum?: string
  payTime?: string
  creditLastDigits?: number
  paymentMethodsId?: number
  refundPercentage?: number
  refund?: Price
  referralAds: Array<ReferralAds> = []

  isCustomerFilled?: boolean
  isStoreFilled?: boolean
  isOverDeadline?: boolean
  // TODO: 型態確認
  customerScore?: any

  paymentMethod?: PaymentMethod
  paymentType?: PaymentType

  /** 2.0 訂單 */
  isReservation: boolean = false

  constructor (order: OrderArgument) {
    this.key = Symbol('')
    if (!isObject(order)) throw new TypeError('`order` must be an object!')

    if (order.orderNum && isString(order.orderNum)) {
      this.orderNum = order.orderNum
      // 預設將 orderNum 設為 erpOrderNum
      this.erpOrderNum = order.orderNum
    }
    // 若 args 有 erpOrderNum，則將 erpOrderNum 設為 erpOrderNum
    if (order.erpOrderNum && isString(order.erpOrderNum)) this.erpOrderNum = order.erpOrderNum
    if (order.startStoreId && (isNumber(order.startStoreId) || isString(order.startStoreId))) this.startStoreId = order.startStoreId
    if (order.startStoreName && isString(order.startStoreName)) this.startStoreName = order.startStoreName
    if (order.endStoreId && (isNumber(order.endStoreId) || isString(order.endStoreId))) this.endStoreId = order.endStoreId
    if (order.endStoreName && isString(order.endStoreName)) this.endStoreName = order.endStoreName

    if (order.car_id && isNumber(order.car_id)) this.carId = order.car_id

    if (order.orderTime && isString(order.orderTime)) this.orderTime = order.orderTime
    if (order.startTime && isString(order.startTime)) this.startTime = order.startTime
    if (order.endTime && isString(order.endTime)) this.endTime = order.endTime
    if (order.cancel_at && isString(order.cancel_at)) this.cancelAt = order.cancel_at
    if (order.insuranceFee && isNumber(order.insuranceFee)) this.insuranceFee = order.insuranceFee
    if (order.status && isString(order.status)) this.status = order.status

    if (order.vendor && isString(order.vendor)) this.vendor = order.vendor
    if (order.productName && isString(order.productName)) this.productName = order.productName
    if (order.email && isString(order.email)) this.email = order.email
    if (order.price && isObject(order.price)) this.price = new Price(order.price.value, order.price.currency)
    if (order.insuranceDataFiled && isNumber(order.insuranceDataFiled)) this.insuranceDataFiled = !!order.insuranceDataFiled
    if (isNumber(order.paid)) this.paid = order.paid
    if (order.insuranceIndex && isString(order.insuranceIndex)) this.insuranceIndex = order.insuranceIndex

    if (order.invoice_to && isString(order.invoice_to)) this.invoiceTo = order.invoice_to
    if (order.invoice_code && isNumber(order.invoice_code)) this.invoiceCode = order.invoice_code
    if (order.invoice_name && isString(order.invoice_name)) this.invoiceName = order.invoice_name
    if (order.invoice_no && isString(order.invoice_no)) this.invoiceNum = order.invoice_no

    if (order.pay_time && isString(order.pay_time)) this.payTime = order.pay_time
    if (order.credit_last_digits && isNumber(order.credit_last_digits)) this.creditLastDigits = order.credit_last_digits
    if (order.payment_methods_id && isNumber(order.payment_methods_id)) this.paymentMethodsId = order.payment_methods_id
    if (order.refundPercentage) this.refundPercentage = order.refundPercentage
    if (order.refund && isObject(order.refund)) this.refund = new Price(order.refund?.value, order.refund?.currency)

    if (order.referralAds && isArray(order.referralAds)) this.referralAds = order.referralAds

    if (isBoolean(order.isCustomerFilled)) this.isCustomerFilled = order.isCustomerFilled
    if (isBoolean(order.isStoreFilled)) this.isStoreFilled = order.isStoreFilled
    if (isBoolean(order.isOverDeadline)) this.isOverDeadline = order.isOverDeadline
    if (order.customerScore) this.customerScore = order.customerScore

    if (order.payment_methods_id && isNumber(order.payment_methods_id)) this.paymentMethod = order.payment_methods_id
    if (order.payment_type && isNumber(order.payment_type)) this.paymentType = order.payment_type

    if (isBoolean(order.isReservation)) this.isReservation = order.isReservation ?? false
  }
}

/**
 * 2.0 訂單 adapter
 */
export class OrderUtils {
  static parseReservation (reservation: ReservationResponse): Order {
    const order = new Order({
      orderNum: reservation.serialNumber,
      erpOrderNum: reservation.erpSerialNumber,
      // TODO: 2.0 shop id 與 1.0 erp id 不互通，需要用 sku 做 mapping
      startStoreId: reservation.shopInfo.sku ? this.parseStoreId(reservation.shopInfo.erpId, reservation.shopInfo.sku, reservation.shopInfo.id) : reservation.shopInfo.id,
      startStoreName: reservation.shopInfo.name,
      // TODO: 2.0 shop id 與 1.0 erp id 不互通，需要用 sku 做 mapping
      endStoreId: reservation.shopInfo.sku ? this.parseStoreId(reservation.shopInfo.erpId, reservation.shopInfo.sku, reservation.shopInfo.id) : reservation.shopInfo.id, // Assuming same store for now
      endStoreName: reservation.shopInfo.name, // Assuming same store for now
      orderTime: dayjs(reservation.createdAt).format('YYYY-MM-DD'),
      startTime: dayjs(reservation.localPickUp).format('YYYY-MM-DD HH:mm'),
      endTime: dayjs(reservation.localDropOff).format('YYYY-MM-DD HH:mm'),
      email: reservation.userInfo.email,
      price: this.parsePrice(reservation),
      // hertz 的 paid 始終為 0
      paid: reservation.shopInfo.erpId === Erp.Hertz ? 0 : (reservation.paidAt ? 1 : 0),
      ...(reservation.paymentInfo?.method && {
        payment_methods_id: this.mappingPaymentMethod(reservation.paymentInfo.method),
      }),
      ...(reservation.shopInfo.erpId !== Erp.Hertz && reservation.paymentInfo?.cardVender && {
        payment_type: this.mappingPaymentType(reservation.paymentInfo.cardVender),
      }),
      vendor: this.mappingVendor(reservation.shopInfo.erpId),
      productName: reservation.carInfo.name,
      referralAds: reservation.referralInfo,
      refundPercentage: this.parseApiResponseReservationRefundPrice(reservation).percentage,
      refund: this.parseApiResponseReservationRefundPrice(reservation).price,
      status: this.parseReservationStatusFromApiReservation(reservation),
      isReservation: true,
    })

    return order
  }

  static mappingPaymentMethod (paymentMethod: ReservationPaymentMethod): PaymentMethod {
    switch (paymentMethod) {
      case ReservationPaymentMethod.CreditCard:
        return PaymentMethod.CreditCard
      case ReservationPaymentMethod.DebitCard:
        return PaymentMethod.DebitCard
      case ReservationPaymentMethod.LinePay:
        return PaymentMethod.LinePay
      case ReservationPaymentMethod.Aftee:
        return PaymentMethod.Aftee
      default:
        return PaymentMethod.Unknown
    }
  }

  static mappingPaymentType (paymentType: CardPaymentVender): PaymentType {
    switch (paymentType) {
      case CardPaymentVender.TapPay:
        return PaymentType.TAPPAY
      case CardPaymentVender.NewebPay:
        return PaymentType.NEWEBPAY
      case CardPaymentVender['91App']:
        return PaymentType.PAYMENTS91APP
      default:
        return PaymentType.UNSPECIFIED
    }
  }

  static mappingVendor (vendor: Erp): Vendor {
    switch (vendor) {
      case Erp.OnlineTravel:
        return Vendor.OTHER
      case Erp.Gogoout_16:
        return Vendor.CAR
      case Erp.Gogoout_18:
        return Vendor.CAR
      case Erp.Gogoout_20:
        return Vendor.CAR
      case Erp.Hertz:
        return Vendor.HERTZ
      default:
        return Vendor.OTHER
    }
  }

  /**
   * 解析 API Reservation 各種價格
   * @param response 後端返回的 Reservation 資訊
   * @returns 車輛價格、加購項目價格、折扣價格
   */
  static parseApiResponseReservationPrices (
    response: ReservationResponse,
  ): Record<'carPrice' | 'optionalItemsPrice' | 'discountPrice', Price> {
    const carPriceCurrencyCode = response.carInfo.saleMoney.currencyCode
    // 車輛價格
    const carPrice = response.carInfo.saleMoney.amount

    // 加價購金額
    const optionalItemsPrice =
      response.optionalInfo?.items?.reduce(
        (acc, item) => {
          return acc + item.optionalMoney.amount
        },
        0,
      ) ??
      0

    // 折扣價格
    const discountPrice = (() => {
      if (response.discountInfo.type === DiscountMethod.None) return 0
      if (response.discountInfo.type === DiscountMethod.Code) return response.discountInfo.promotion.discountMoney.amount
      if (response.discountInfo.type === DiscountMethod.Coupon) return response.discountInfo.coupon?.discountMoney.amount ?? 0
      if (response.discountInfo.type === DiscountMethod.GoPoint) return response.discountInfo.goPoints.discountMoney.amount
      return 0
    })()

    return {
      carPrice: new Price(carPrice, { code: carPriceCurrencyCode, sign: '$' }),
      optionalItemsPrice: new Price(optionalItemsPrice, { code: carPriceCurrencyCode, sign: '$' }),
      discountPrice: new Price(discountPrice, { code: carPriceCurrencyCode, sign: '$' }),
    }
  }

  static parsePrice (reservation: ReservationResponse): Price {
    const { carPrice, optionalItemsPrice, discountPrice } = this.parseApiResponseReservationPrices(reservation)
    return new Price(carPrice.value + optionalItemsPrice.value - discountPrice.value, carPrice.currency)
  }

  /**
   * 從 API 回傳的訂單資訊中解析訂單狀態
   * @param apiReservation 訂單資訊
   * @returns 訂單狀態
   */
  static parseReservationStatusFromApiReservation (
    apiReservation: ReservationResponse,
  ): OrderStatus {
    const shopTimezone = apiReservation.shopInfo.timezone
    // 店家時區的當下時間
    const now = dayjs.tz(new Date(), shopTimezone)
    // 店家時區的取車時間
    const pickUpTime = dayjs.tz(apiReservation.localPickUp, shopTimezone)
    // 店家時區的還車時間
    const dropOffTime = dayjs.tz(apiReservation.localDropOff, shopTimezone)

    /**
     * 訂單狀態
     */
    const status = reverseReservationStatus(
      apiReservation.status,
    )
    const paidFailed = status === ApiReservationStatus.PAYMENT_FAILURE

    /**
     * 透過時間戳來判斷哪些狀態已發生
     */
    const refunded = !!apiReservation.refundedAt
    const expired = !!apiReservation.expiredAt
    const reserved = !!apiReservation.reservedAt
    const cancelled = !!apiReservation.cancelledAt

    /**
     * 失敗:
     * - 付款失敗
     * - 逾期
     * - 已退款
     */
    if (paidFailed || expired || refunded) {
      return 'canceled'
    }

    // Canceled: 已取消
    if (cancelled) {
      return 'canceled'
    }

    // Processing: 處理中
    if (!reserved) {
      return 'alive'
    }

    // Alive: 預定成功並尚未取車
    if (reserved && pickUpTime.isAfter(now)) {
      return 'alive'
    }

    // Delivered: 已取車
    if (reserved && pickUpTime.isBefore(now) && dropOffTime.isAfter(now)) {
      return 'delivered'
    }

    // Complete: 已完成
    if (reserved && dropOffTime.isBefore(now)) {
      return 'complete'
    }

    return 'canceled'
  }

  /**
   * 解析 API Reservation Refund
   * @param reservation 後端返回的 Reservation 資訊
   * @returns ReservationRefund
   */
  static parseApiResponseReservationRefundPrice (
    reservation: ReservationResponse,
  ): {price: {
    value: number,
    currency: {
      code: string,
      sign: string,
    }
  }, percentage: number} {
    const reservationStatus =
      this.parseReservationStatusFromApiReservation(reservation)

    const refundPercentage = (() => {
      // 手動取消
      if (reservationStatus === 'canceled') {
        return reservation.cancelInfo?.selectedRule?.refundPercent ?? (reservation.cancelInfo?.autoRefundMoney ? 100 : 0)
      }

      return 0
    })()

    const refundPrice = (() => {
      // 手動取消
      if (reservationStatus === 'canceled') {
        return {
          value: reservation.cancelInfo?.selectedRule?.refundMoney.amount ?? reservation.cancelInfo?.autoRefundMoney?.amount ?? 0,
          currency: {
            code: reservation.cancelInfo?.selectedRule?.refundMoney.currencyCode ?? reservation.cancelInfo?.autoRefundMoney?.currencyCode ?? 'TWD',
            sign: '$',
          },
        }
      }

      return {
        value: 0,
        currency: {
          code: 'TWD',
          sign: '$',
        },
      }
    })()

    return {
      price: refundPrice,
      percentage: refundPercentage,
    }
  }

  /**
   * 解析店家 ID，將 gogoout ERP `1_123` 格式的 SKU 轉換為數字，即 `123`，其他供應商則回傳 shopId
   * @param erpId 供應商 ID
   * @param sku 店家 SKU
   * @param shopId Resell 店家 ID
   * @returns 解析後的店家 ID
   */
  static parseStoreId (erpId: Erp, sku: string, shopId: number): number {
    switch (erpId) {
      case Erp.Gogoout_16:
        return sku.split('_')[1] ? Number(sku.split('_')[1]) : shopId
      case Erp.Gogoout_18:
        return sku.split('_')[1] ? Number(sku.split('_')[1]) : shopId
      case Erp.Gogoout_20:
        return sku.split('_')[1] ? Number(sku.split('_')[1]) : shopId
      default:
        return shopId
    }
  }
}
